/**
 * Medical / Training Institute listings (third account type, role "institute").
 *
 * Firestore: institutes/{uid} — one listing per institute account, doc id == owner uid.
 * Public read (it's a directory EMTs are sent to); only the owning institute account writes it.
 *
 * Recommendation is deterministic — no AI: an EMT missing certification X is shown institutes
 * whose `programs` include X (or a match-equivalent cert, e.g. EMT-B ≡ NREMT-B), ranked
 * same city → within INSTITUTE_RECOMMENDATION_RADIUS_MILES (when both sides have coordinates)
 * → same state, measured to the institute's NEAREST location. Institutes elsewhere are never
 * recommended.
 *
 * Listing shape (2026-09-23): besides the signup fields, an institute keeps
 *  - programOfferings[]: the programs it teaches, each for a certification (canonical where known),
 *    initial / renewal / both, format, duration, price and notes. `programs` (canonical names) is
 *    derived from them on every save — it is what the array-contains-any lookup and the server's
 *    renewal matching read.
 *  - locations[]: every campus; the first is the primary and is mirrored to the top-level
 *    city / state / zip / geo the older readers use.
 *  - logoUrl: an image under avatars/{uid}/ (public-read Storage path the owner may write).
 * Listings written before this (signup only) have neither array; readers derive them from the
 * legacy fields, so nothing needs a migration.
 */
import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { appCheckSettled, db, storage } from './firebase';
import { EMT_CERTIFICATIONS, certificationMatchKey, resolveCanonicalCertification } from './constants/certifications';
import { haversineMiles } from './profileCoordinates';
import { isValidUsLocation } from './maps/usLocationValidation';
import { getGeoForCityState } from './geoCache';
import { isDemoViewer, isHiddenDemoOrg } from './demoAccounts';

export const INSTITUTES_COLLECTION = 'institutes';
export const INSTITUTE_RECOMMENDATION_RADIUS_MILES = 100;

export type InstituteDraft = {
  name: string;
  /** Canonical EMT_CERTIFICATIONS values the institute trains for. */
  programs: string[];
  /** Free text for programs outside the canonical list (CEUs, EVOC variants, etc.). */
  otherPrograms: string;
  city: string;
  state: string;
  zip: string;
  geo: { lat: number; lng: number } | null;
  website: string;
  contactEmail: string;
  phone: string;
  description: string;
};

export type InstituteProgramKind = 'initial' | 'renewal' | 'both';
export type InstituteProgramFormat = 'in_person' | 'hybrid' | 'online' | '';

export type InstituteProgram = {
  id: string;
  /** Canonical certification name (BLS, ACLS …) when it is one; otherwise the program's own name. */
  certification: string;
  /** Optional display title, e.g. "BLS Provider Renewal (2-hour)". */
  title: string;
  kind: InstituteProgramKind;
  format: InstituteProgramFormat;
  duration: string;
  price: string;
  notes: string;
};

export type InstituteLocation = {
  id: string;
  label: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  geo: { lat: number; lng: number } | null;
};

export type Institute = InstituteDraft & {
  id: string;
  ownerUid: string;
  isVerified?: boolean;
  /** Server-owned review state (functions/src/instituteReview.ts); see lib/institutes/instituteReview.ts. */
  reviewStatus?: 'pending' | 'approved' | 'revoked';
  reviewRequestedAt?: unknown;
  reviewedAt?: unknown;
  /** Admin-set demo containment flag (same rule as demo employers: listed only for demo viewers). */
  isDemoAccount?: boolean;
  logoUrl?: string | null;
  programOfferings?: InstituteProgram[];
  locations?: InstituteLocation[];
  createdAt?: unknown;
  updatedAt?: unknown;
};

export const PROGRAM_KIND_LABELS: Record<InstituteProgramKind, string> = {
  initial: 'Initial certification',
  renewal: 'Renewal / recertification',
  both: 'Initial and renewal',
};

export const PROGRAM_FORMAT_LABELS: Record<Exclude<InstituteProgramFormat, ''>, string> = {
  in_person: 'In person',
  hybrid: 'Hybrid',
  online: 'Online',
};

export function emptyInstituteDraft(): InstituteDraft {
  return {
    name: '',
    programs: [],
    otherPrograms: '',
    city: '',
    state: '',
    zip: '',
    geo: null,
    website: '',
    contactEmail: '',
    phone: '',
    description: '',
  };
}

export function newLocalId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

export function emptyInstituteProgram(): InstituteProgram {
  return { id: newLocalId('prog'), certification: '', title: '', kind: 'both', format: '', duration: '', price: '', notes: '' };
}

export function emptyInstituteLocation(): InstituteLocation {
  return { id: newLocalId('loc'), label: '', address: '', city: '', state: '', zip: '', geo: null };
}

export function normalizePrograms(programs: string[]): string[] {
  const out = new Set<string>();
  for (const p of programs) {
    const canonical = resolveCanonicalCertification(p);
    if (canonical) out.add(canonical);
  }
  return [...out];
}

export function validateInstituteDraft(d: InstituteDraft): string | null {
  if (!d.name.trim()) return 'Enter your institute or company name.';
  if (normalizePrograms(d.programs).length === 0 && !d.otherPrograms.trim()) {
    return 'Select at least one certification or program you offer.';
  }
  if (!isValidUsLocation(d.city.trim(), d.state.trim().toUpperCase())) return 'Enter your city and state.';
  if (d.website.trim() && !/^https?:\/\/\S+\.\S+/i.test(d.website.trim())) {
    return 'Website must start with http:// or https://';
  }
  return null;
}

export function instituteToDraft(i: Partial<Institute>): InstituteDraft {
  const base = emptyInstituteDraft();
  return {
    name: i.name ?? base.name,
    programs: Array.isArray(i.programs) ? i.programs : base.programs,
    otherPrograms: i.otherPrograms ?? base.otherPrograms,
    city: i.city ?? base.city,
    state: i.state ?? base.state,
    zip: i.zip ?? base.zip,
    geo: i.geo && typeof i.geo.lat === 'number' && typeof i.geo.lng === 'number' ? i.geo : null,
    website: i.website ?? base.website,
    contactEmail: i.contactEmail ?? base.contactEmail,
    phone: i.phone ?? base.phone,
    description: i.description ?? base.description,
  };
}

function validGeo(g: unknown): { lat: number; lng: number } | null {
  const v = g as { lat?: unknown; lng?: unknown } | null | undefined;
  return v && typeof v.lat === 'number' && typeof v.lng === 'number' && Number.isFinite(v.lat) && Number.isFinite(v.lng)
    ? { lat: v.lat, lng: v.lng }
    : null;
}

/** The institute's programs; a signup-only listing gets one entry per checked certification. */
export function instituteProgramOfferings(i: Partial<Institute>): InstituteProgram[] {
  if (Array.isArray(i.programOfferings) && i.programOfferings.length > 0) {
    return i.programOfferings.map((p, idx) => ({ ...emptyInstituteProgram(), ...p, id: p.id || `prog_${idx}` }));
  }
  const fromChecked = (Array.isArray(i.programs) ? i.programs : []).map((cert, idx) => ({
    ...emptyInstituteProgram(),
    id: `prog_legacy_${idx}`,
    certification: cert,
  }));
  const other = (i.otherPrograms || '').trim();
  return other
    ? [...fromChecked, { ...emptyInstituteProgram(), id: 'prog_legacy_other', certification: other, title: other }]
    : fromChecked;
}

/** Every location; a signup-only listing has exactly one, built from the top-level fields. */
export function instituteLocations(i: Partial<Institute>): InstituteLocation[] {
  if (Array.isArray(i.locations) && i.locations.length > 0) {
    return i.locations.map((l, idx) => ({ ...emptyInstituteLocation(), ...l, id: l.id || `loc_${idx}`, geo: validGeo(l.geo) }));
  }
  if (!i.city && !i.state) return [];
  return [{ id: 'loc_primary', label: '', address: '', city: i.city || '', state: i.state || '', zip: i.zip || '', geo: validGeo(i.geo) }];
}

export function programDisplayName(p: InstituteProgram): string {
  return (p.title || '').trim() || (p.certification || '').trim() || 'Program';
}

export async function getInstitute(id: string): Promise<Institute | null> {
  if (!db || !id) return null;
  // /institutes/:id is opened signed out (from an EMT's link, a message, a search result). Its first
  // read must not race App Check start-up, or an enforcing project refuses it as permission-denied
  // and the page says "not available" — same rule as the public job pages (firestore/jobs.ts).
  await appCheckSettled;
  const snap = await getDoc(doc(db, INSTITUTES_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<Institute, 'id'>) };
}

async function withGeo<T extends { city: string; state: string; geo: { lat: number; lng: number } | null }>(loc: T): Promise<T> {
  if (loc.geo || !isValidUsLocation(loc.city.trim(), loc.state.trim().toUpperCase())) return loc;
  // Without coordinates an institute is only ever matched by same city / same state — look the
  // city up (city-level, cached) so "within N miles" works for it too.
  const coords = await getGeoForCityState(loc.city.trim(), loc.state.trim().toUpperCase()).catch(() => null);
  return coords ? { ...loc, geo: { lat: coords.lat, lng: coords.lng } } : loc;
}

/** Creates or updates the caller's own listing (doc id == uid). Used by signup. */
export async function saveInstitute(uid: string, d: InstituteDraft, opts?: { isNew?: boolean }): Promise<void> {
  if (!db) throw new Error('Firestore not initialized');
  const located = await withGeo({ city: d.city, state: d.state, geo: d.geo });
  await setDoc(
    doc(db, INSTITUTES_COLLECTION, uid),
    {
      ownerUid: uid,
      name: d.name.trim(),
      programs: normalizePrograms(d.programs),
      otherPrograms: d.otherPrograms.trim(),
      city: d.city.trim(),
      state: d.state.trim().toUpperCase(),
      zip: d.zip.trim(),
      geo: located.geo ? { lat: located.geo.lat, lng: located.geo.lng } : null,
      website: d.website.trim(),
      contactEmail: d.contactEmail.trim(),
      phone: d.phone.trim(),
      description: d.description.trim(),
      updatedAt: serverTimestamp(),
      ...(opts?.isNew ? { createdAt: serverTimestamp(), isVerified: false } : {}),
    },
    { merge: true },
  );
}

export type InstituteProfileDraft = {
  name: string;
  description: string;
  website: string;
  contactEmail: string;
  phone: string;
  logoUrl: string | null;
};

export function validateInstituteProfile(p: InstituteProfileDraft): string | null {
  if (!p.name.trim()) return 'Enter your institute or company name.';
  if (p.website.trim() && !/^https?:\/\/\S+\.\S+/i.test(p.website.trim())) return 'Website must start with http:// or https://';
  if (p.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.contactEmail.trim())) return 'Enter a valid enrollment email.';
  if (p.description.length > 2000) return 'Keep the About section under 2,000 characters.';
  return null;
}

export async function saveInstituteProfile(uid: string, p: InstituteProfileDraft): Promise<void> {
  if (!db) throw new Error('Firestore not initialized');
  await setDoc(
    doc(db, INSTITUTES_COLLECTION, uid),
    {
      ownerUid: uid,
      name: p.name.trim(),
      description: p.description.trim(),
      website: p.website.trim(),
      contactEmail: p.contactEmail.trim(),
      phone: p.phone.trim(),
      logoUrl: p.logoUrl || null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export function validateInstitutePrograms(programs: InstituteProgram[]): string | null {
  if (programs.length === 0) return 'Add at least one program.';
  for (const p of programs) {
    if (!p.certification.trim()) return 'Choose the certification each program teaches.';
    if (programDisplayName(p).length > 120) return 'Keep program titles under 120 characters.';
    if (p.notes.length > 600) return 'Keep program notes under 600 characters.';
  }
  return null;
}

/** Saves the program list and re-derives `programs` (canonical names) and `otherPrograms` from it. */
export async function saveInstitutePrograms(uid: string, programs: InstituteProgram[]): Promise<void> {
  if (!db) throw new Error('Firestore not initialized');
  const clean = programs.map((p) => {
    const canonical = resolveCanonicalCertification(p.certification);
    return {
      id: p.id,
      certification: canonical ?? p.certification.trim(),
      title: p.title.trim(),
      kind: p.kind,
      format: p.format,
      duration: p.duration.trim(),
      price: p.price.trim(),
      notes: p.notes.trim(),
    };
  });
  const canonical = normalizePrograms(clean.map((p) => p.certification));
  const other = clean.filter((p) => !resolveCanonicalCertification(p.certification)).map(programDisplayName);
  await setDoc(
    doc(db, INSTITUTES_COLLECTION, uid),
    {
      ownerUid: uid,
      programOfferings: clean,
      programs: canonical,
      otherPrograms: other.join(', '),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export function validateInstituteLocations(locations: InstituteLocation[]): string | null {
  if (locations.length === 0) return 'Add at least one location.';
  for (const l of locations) {
    if (!isValidUsLocation(l.city.trim(), l.state.trim().toUpperCase())) return 'Every location needs a city and state.';
  }
  return null;
}

/** Saves every location (geocoding any without coordinates); the first is mirrored to the top-level fields. */
export async function saveInstituteLocations(uid: string, locations: InstituteLocation[]): Promise<InstituteLocation[]> {
  if (!db) throw new Error('Firestore not initialized');
  const located: InstituteLocation[] = [];
  for (const l of locations) {
    const trimmed = {
      id: l.id,
      label: l.label.trim(),
      address: l.address.trim(),
      city: l.city.trim(),
      state: l.state.trim().toUpperCase(),
      zip: l.zip.trim(),
      geo: validGeo(l.geo),
    };
    located.push(await withGeo(trimmed));
  }
  const primary = located[0];
  await setDoc(
    doc(db, INSTITUTES_COLLECTION, uid),
    {
      ownerUid: uid,
      locations: located,
      city: primary.city,
      state: primary.state,
      zip: primary.zip,
      geo: primary.geo,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  return located;
}

const LOGO_TYPES: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };
export const INSTITUTE_LOGO_MAX_BYTES = 2 * 1024 * 1024;

/** Uploads a logo to avatars/{uid}/ (public read, owner write — storage.rules) and returns its URL. */
export async function uploadInstituteLogo(uid: string, file: File): Promise<string> {
  if (!storage) throw new Error('Storage not initialized');
  const ext = LOGO_TYPES[file.type];
  if (!ext) throw new Error('Use a PNG, JPG or WebP image.');
  if (file.size > INSTITUTE_LOGO_MAX_BYTES) throw new Error('Logo must be 2 MB or smaller.');
  const objectRef = ref(storage, `avatars/${uid}/institute-logo-${Date.now()}.${ext}`);
  await uploadBytes(objectRef, file, { contentType: file.type });
  return getDownloadURL(objectRef);
}

/** Every canonical cert that satisfies `certName` for matching (EMT-B ≡ NREMT-B, …). */
export function equivalentCertNames(certName: string): string[] {
  const key = certificationMatchKey(certName);
  return EMT_CERTIFICATIONS.filter((c) => certificationMatchKey(c) === key);
}

/**
 * THE rule for "may an EMT be pointed to this institute": Arevina has reviewed it (isVerified, which
 * firestore.rules forbids the institute from setting), and it is not a contained demo / TEST listing
 * for this viewer. Every EMT-facing list — "train nearby", "Renew your certification", the job page's
 * "where to get it" and Find training — goes through it, so an account that merely signed up as an
 * institute is never recommended to anyone. Opening a listing by its link is unaffected.
 */
export function isDiscoverableInstitute(inst: Partial<Institute> | null | undefined): boolean {
  return Boolean(inst) && inst?.isVerified === true && !isHiddenDemoOrg(inst);
}

/** Where an EMT browses every reviewed institute; `cert` pre-selects the credential filter. */
export const FIND_TRAINING_PATH = '/find-training';
export function findTrainingHref(cert?: string | null): string {
  const name = (cert || '').trim();
  return name ? `${FIND_TRAINING_PATH}?cert=${encodeURIComponent(resolveCanonicalCertification(name) ?? name)}` : FIND_TRAINING_PATH;
}

/**
 * Reviewed institutes that teach `certName`. ONE rule with Find training (2026-10-15, tester fixes A4):
 * the same list (listDiscoverableInstitutes) and the same "teaches" test (instituteTeaches, on the
 * program offerings). Before, this queried the legacy `programs` array while Find training read
 * `programOfferings`, so the job page's "How to get it" and Find training could disagree.
 */
export async function listInstitutesOfferingCert(certName: string): Promise<Institute[]> {
  if (!certName.trim()) return [];
  const all = await listDiscoverableInstitutes();
  return all.filter((inst) => instituteTeaches(inst, certName));
}

const DISCOVERABLE_TTL_MS = 60_000;
let discoverableCache: { at: number; demo: boolean; promise: Promise<Institute[]> } | null = null;

/**
 * Every institute an EMT may be shown: reviewed by Arevina (isVerified), and not a demo or test
 * institute unless the viewer is a demo account (isDiscoverableInstitute). Shared for a minute, so a
 * job page with several missing credentials reads once.
 */
export async function listDiscoverableInstitutes(): Promise<Institute[]> {
  if (!db) return [];
  const demo = isDemoViewer();
  if (discoverableCache && discoverableCache.demo === demo && Date.now() - discoverableCache.at < DISCOVERABLE_TTL_MS) return discoverableCache.promise;
  const promise = (async () => {
    await appCheckSettled;
    const snap = await getDocs(query(collection(db, INSTITUTES_COLLECTION), where('isVerified', '==', true), limit(300)));
    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as Omit<Institute, 'id'>) }))
      .filter(isDiscoverableInstitute);
  })();
  discoverableCache = { at: Date.now(), demo, promise };
  promise.catch(() => {
    if (discoverableCache?.promise === promise) discoverableCache = null;
  });
  return promise;
}

/** True when one of the institute's programs teaches `certName` (or a match-equivalent certification). */
export function instituteTeaches(inst: Partial<Institute>, certName: string): boolean {
  const key = certificationMatchKey(certName);
  if (!key) return false;
  return instituteProgramOfferings(inst).some((p) => certificationMatchKey(p.certification) === key);
}

export type InstituteProximity = {
  /** The campus closest to the EMT; the primary campus when no distance can be measured. */
  location: InstituteLocation | null;
  /** Miles to that campus, or null when the EMT or every campus lacks coordinates. */
  distanceMiles: number | null;
  sameState: boolean;
};

/**
 * Nearest campus to the EMT, at ANY distance (rankInstitutesForEmt drops institutes that are not
 * nearby — right for a recommendation, wrong for a directory the EMT filters themselves).
 */
export function instituteProximity(inst: Partial<Institute>, emt: EmtLocation): InstituteProximity {
  const locations = instituteLocations(inst);
  const emtState = (emt.state || '').trim().toUpperCase();
  const hasEmtGeo = typeof emt.lat === 'number' && typeof emt.lng === 'number';
  let best: InstituteProximity = {
    location: locations[0] ?? null,
    distanceMiles: null,
    sameState: Boolean(emtState) && locations.some((l) => (l.state || '').trim().toUpperCase() === emtState),
  };
  if (!hasEmtGeo) return best;
  for (const loc of locations) {
    if (!loc.geo) continue;
    const miles = haversineMiles(emt.lat as number, emt.lng as number, loc.geo.lat, loc.geo.lng);
    if (best.distanceMiles === null || miles < best.distanceMiles) best = { ...best, location: loc, distanceMiles: miles };
  }
  return best;
}

export type EmtLocation = {
  lat?: number | null;
  lng?: number | null;
  city?: string | null;
  state?: string | null;
};

export type InstituteRecommendation = {
  institute: Institute;
  /** The institute location the EMT is closest to (the one the distance and reason refer to). */
  location: InstituteLocation | null;
  distanceMiles: number | null;
  reason: 'same_city' | 'nearby' | 'same_state';
};

export function rankInstitutesForEmt(
  institutes: Institute[],
  emt: EmtLocation,
  radiusMiles: number = INSTITUTE_RECOMMENDATION_RADIUS_MILES,
): InstituteRecommendation[] {
  const emtState = (emt.state || '').trim().toUpperCase();
  const emtCity = (emt.city || '').trim().toLowerCase();
  const hasEmtGeo = typeof emt.lat === 'number' && typeof emt.lng === 'number';
  const rank = { same_city: 0, nearby: 1, same_state: 2 } as const;
  const recs: InstituteRecommendation[] = [];

  for (const inst of institutes) {
    let best: InstituteRecommendation | null = null;
    for (const loc of instituteLocations(inst)) {
      const locState = (loc.state || '').trim().toUpperCase();
      const sameState = Boolean(emtState) && locState === emtState;
      const sameCity = sameState && Boolean(emtCity) && (loc.city || '').trim().toLowerCase() === emtCity;
      const distance =
        hasEmtGeo && loc.geo ? haversineMiles(emt.lat as number, emt.lng as number, loc.geo.lat, loc.geo.lng) : null;
      let rec: InstituteRecommendation | null = null;
      if (sameCity) rec = { institute: inst, location: loc, distanceMiles: distance, reason: 'same_city' };
      else if (distance !== null && distance <= radiusMiles) rec = { institute: inst, location: loc, distanceMiles: distance, reason: 'nearby' };
      else if (sameState) rec = { institute: inst, location: loc, distanceMiles: distance, reason: 'same_state' };
      if (!rec) continue;
      if (
        !best ||
        rank[rec.reason] < rank[best.reason] ||
        (rank[rec.reason] === rank[best.reason] && (rec.distanceMiles ?? Infinity) < (best.distanceMiles ?? Infinity))
      ) {
        best = rec;
      }
    }
    if (best) recs.push(best);
  }

  return recs.sort((a, b) => {
    if (rank[a.reason] !== rank[b.reason]) return rank[a.reason] - rank[b.reason];
    if (a.distanceMiles !== null && b.distanceMiles !== null && a.distanceMiles !== b.distanceMiles) {
      return a.distanceMiles - b.distanceMiles;
    }
    if (a.distanceMiles !== null && b.distanceMiles === null) return -1;
    if (a.distanceMiles === null && b.distanceMiles !== null) return 1;
    return (a.institute.name || '').localeCompare(b.institute.name || '');
  });
}
